import React, { useMemo, useRef } from 'react';
import useClassNames from 'helpers/hooks/useClassNames';
import { resolveReferenceProps, resolveReferenceTarget } from 'helpers/reference';
import { Link as NextLink, useIntlRouter } from 'i18n/routing';
import { usePageLoader } from 'providers/loader';
import { Reference } from 'types/reference';
export type LinkVariant = 'primary' | 'menu-header' | 'menu-item' | 'breadcrumb' | 'footer';

export interface LinkProps extends Omit<React.ComponentProps<typeof NextLink>, 'href' | 'key'> {
  link?: string | Reference;
  variant?: LinkVariant;
  considerQueryParams?: boolean;
}

type VariantStyle = { [key in LinkVariant]: string };

const variantStyle: VariantStyle = {
  primary: 'text-14 lg:text-16 cursor-pointer text-neutral-500 hover:text-neutral-400',
  'menu-item': 'text-primary hover:text-gray-500',
  breadcrumb: 'text-14 font-medium text-primary',
  'menu-header': 'text-14 font-medium text-primary hover:text-gray-500 cursor-pointer',
  footer: 'text-14',
};

const Link = ({
  link,
  children,
  className = '',
  variant,
  title = '',
  considerQueryParams = false,
  onClick,
  ...props
}: React.PropsWithChildren<LinkProps>) => {
  const linkUrl = useMemo(() => {
    if (!link) return '';
    if (link === 'link') return '';
    if (typeof link === 'string') return link;

    return resolveReferenceTarget(link);
  }, [link]);

  const linkProps = useMemo(() => {
    if (!link || typeof link === 'string') return {};

    return resolveReferenceProps(link);
  }, [link]);

  const linkClassNames = useClassNames([variant ? variantStyle[variant] : '', className]);
  const { startNavigation } = usePageLoader();
  const router = useIntlRouter();
  const destinationRef = useRef<string>('');
  const navigationProps = { ...props, ...linkProps };

  return (
    <NextLink
      onNavigate={(e) => {
        if (!linkUrl) return;

        // Compare the rendered href: Next/next-intl have already resolved basePath and locale.
        const current = new URL(globalThis.location.href);
        const target = new URL(destinationRef.current || linkUrl, current);
        const normalizePath = (path: string) => path.replace(/\/$/, '') || '/';
        const samePath =
          current.origin === target.origin && normalizePath(current.pathname) === normalizePath(target.pathname);
        current.searchParams.sort();
        target.searchParams.sort();
        const sameSearch = current.search === target.search;
        const compareSearch = considerQueryParams || Boolean(target.search);

        if (samePath && current.hash === target.hash && (!compareSearch || sameSearch)) {
          e.preventDefault();
          // Category clicks must retain loading feedback even at the current URL.
          // Track the refresh in the provider so closing the menu cannot orphan it.
          if (considerQueryParams) startNavigation(() => router.refresh());
          return;
        }

        // Preserve native hash scrolling; it does not need a blocking page loader.
        if (samePath && sameSearch && current.hash !== target.hash) return;

        e.preventDefault();
        const navigate = navigationProps.replace ? router.replace : router.push;
        // The provider outlives dropdown links, which unmount as soon as the menu closes.
        startNavigation(() =>
          navigate(linkUrl, {
            scroll: navigationProps.scroll,
            ...(navigationProps.locale ? { locale: navigationProps.locale } : {}),
          }),
        );
      }}
      {...props}
      {...linkProps}
      onClick={(event) => {
        destinationRef.current = event.currentTarget.href;
        onClick?.(event);
      }}
      href={linkUrl || '#'}
      className={linkClassNames}
      title={title}
    >
      {children}
    </NextLink>
  );
};

export default Link;
